import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useReaderStore } from "@/store/readerStore";
import {
  checkServerTTS,
  checkKokoroTTS,
  fetchKokoroRoster,
  previewVoice,
} from "@/services/ttsService";
import type { TTSProviderName } from "@/types/tts";
import type { ReaderTheme } from "@/types/reader";

interface Props {
  onClose: () => void;
  onOpenChapters?: () => void;
  /** Render inside the bottom bar's panel slot instead of as a
   *  floating overlay: no fixed positioning, no scrim, inherits the
   *  bar's background. 2026-10-06 — Thomas asked for panels to sit on
   *  top of the progress row as part of the bar assembly. */
  inline?: boolean;
}

const ACCENT = "#f7931a";

const FONTS = [
  { id: '"Iowan Old Style", "Palatino Linotype", "Georgia", serif', label: "Serif" },
  { id: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", label: "Sans" },
  { id: '"Charter", "Bitstream Charter", Cambria, serif', label: "Charter" },
  { id: "ui-monospace, SFMono-Regular, Menlo, monospace", label: "Mono" },
];

const THEMES: { id: ReaderTheme; label: string; bg: string; fg: string }[] = [
  { id: "paper", label: "Day",   bg: "#fdfcf8", fg: "#1b1712" },
  { id: "white", label: "White", bg: "#ffffff", fg: "#1a1a1a" },
  { id: "sepia", label: "Sepia", bg: "#f4ecd8", fg: "#5b4636" },
  { id: "gray",  label: "Gray",  bg: "#cfd2cd", fg: "#1a1a1a" },
  { id: "dark",  label: "Dark",  bg: "#0e0e10", fg: "#e8e6e1" },
  { id: "eink-light", label: "E-ink Light", bg: "#ffffff", fg: "#000000" },
  { id: "eink-dark",  label: "E-ink Dark",  bg: "#000000", fg: "#ffffff" },
];

const SLEEP_OPTIONS = [0, 5, 10, 15, 30, 45, 60, 90];

export function SettingsPanel({ onClose, onOpenChapters, inline }: Props) {
  const r = useReaderStore((s) => s.reader);
  const setReader = useReaderStore((s) => s.setReader);
  const setTheme = useReaderStore((s) => s.setTheme);
  const tts = useReaderStore((s) => s.tts);
  const setTTS = useReaderStore((s) => s.setTTS);
  const sleepTimer = useReaderStore((s) => s.sleepTimer);
  const armSleepTimer = useReaderStore((s) => s.armSleepTimer);

  const [serverVoices, setServerVoices] = useState<string[]>([]);
  const [kokoroVoices, setKokoroVoices] = useState<string[]>([]);
  const [kokoroOnline, setKokoroOnline] = useState(false);
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    void checkServerTTS().then((h) => {
      if (h?.voices) setServerVoices(h.voices);
    });
    void checkKokoroTTS().then((h) => {
      if (h?.ok) setKokoroOnline(true);
    });
    void fetchKokoroRoster().then((v) => setKokoroVoices(v));
  }, []);

  const groupedKokoro = useMemo(() => groupKokoroVoices(kokoroVoices), [kokoroVoices]);

  const sleepRemainingMs = sleepTimer.expiresAt
    ? Math.max(0, sleepTimer.expiresAt - now)
    : 0;

  const handlePreview = async (voice: string) => {
    if (previewing === voice) return;
    setPreviewing(voice);
    const label = decodeKokoro(voice)?.name ?? voice;
    try {
      await previewVoice("kokoro", voice, label);
    } catch { /* preview errors are non-fatal */ }
    setPreviewing((v) => (v === voice ? null : v));
  };

  const selectClass =
    "pv-surface pv-border pv-fg max-w-[60%] rounded-lg border px-3 py-1.5 text-right text-[15px] leading-tight";
  const wideSelectClass =
    "pv-surface pv-border pv-fg w-full rounded-lg border px-3 py-2 text-[15px] leading-tight";

  // Four-panel hub (2026-08-21). Instead of one long scroll, the sheet opens on
  // a 4-button chooser — Chapters · View · Voice · Sleep — and each button loads
  // only its own controls, so every screen is short.
  type Tab = null | "view" | "voice" | "sleep";
  const [tab, setTab] = useState<Tab>(null);
  const tabTitle = tab === "view" ? "View" : tab === "voice" ? "Voice" : tab === "sleep" ? "Sleep" : "Settings";

  const viewControls = (
    <Group header="View">
      <Row label="Theme">
        <div className="flex flex-wrap justify-end gap-2.5">
          {THEMES.map((t) => (
            <button
              key={t.id}
              onClick={() => setTheme(t.id)}
              aria-pressed={r.theme === t.id}
              title={t.label}
              className={`flex items-center justify-center rounded-full border transition-shadow ${
                r.theme === t.id ? "ring-2 ring-[#f7931a]" : ""
              }`}
              style={{ width: 32, height: 32, background: t.bg, borderColor: "rgba(0,0,0,0.18)" }}
            >
              <span className="sr-only">{t.label}</span>
            </button>
          ))}
        </div>
      </Row>

      <Row label="Font">
        <div className="flex flex-wrap justify-end gap-1.5">
          {FONTS.map((f) => (
            <button
              key={f.label}
              onClick={() => setReader({ fontFamily: f.id })}
              aria-pressed={r.fontFamily === f.id}
              className={`rounded-lg border px-3 py-1.5 text-[13px] leading-none ${
                r.fontFamily === f.id ? "pv-accent-bg border-transparent font-medium" : "pv-border"
              }`}
              style={{ fontFamily: f.id }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </Row>

      <SliderRow label="Text Size" value={`${r.fontSize} px`}>
        <input
          type="range" min={12} max={64} step={1} value={r.fontSize}
          onChange={(e) => setReader({ fontSize: Number(e.target.value) })}
          className="w-full accent-[#f7931a]"
          aria-label="Text size"
        />
      </SliderRow>

      <Row label="Page buttons">
        <div className="flex flex-wrap justify-end gap-1.5">
          {([["kindle", "Kindle default"], ["reversed", "Reversed"]] as const).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setReader({ buttonOrder: id })}
              aria-pressed={(r.buttonOrder ?? "reversed") === id}
              className={`rounded-lg border px-3 py-1.5 text-[13px] leading-none ${
                (r.buttonOrder ?? "reversed") === id ? "pv-accent-bg border-transparent font-medium" : "pv-border"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </Row>

      {/* 2026-10-06: one-tap route to the key diagnostic. The BOOX side
          buttons either reach the browser as key events or they don't, and
          that is the single fact that decides whether hardware paging can
          work at all in a web app — but #/keytest was only reachable by
          typing a URL, which on an e-reader nobody does. Press the buttons
          on that screen: if lines appear, we can map them; if the screen
          stays empty, Onyx is swallowing them below the browser and no
          amount of JavaScript will help. */}
      <Row label="Page buttons not working?">
        <a
          href="#/keytest"
          className="pv-border rounded-lg border px-3 py-1.5 text-[13px] leading-none"
        >
          Key test
        </a>
      </Row>
    </Group>
  );

  const voiceControls = (
    <Group header="Voice">
      <SliderRow label="Speed" value={`${tts.rate.toFixed(2)}×`}>
        <input
          type="range" min={0.5} max={2} step={0.05} value={tts.rate}
          onChange={(e) => setTTS({ rate: Number(e.target.value) })}
          className="w-full accent-[#f7931a]"
          aria-label="Speech speed"
        />
      </SliderRow>

      <Row label="Engine">
        <select
          value={tts.provider}
          onChange={(e) => setTTS({ provider: e.target.value as TTSProviderName, serverVoice: undefined })}
          className={selectClass}
          aria-label="TTS engine"
        >
          <option value="kokoro" disabled={!kokoroOnline}>Kokoro {kokoroOnline ? "(default)" : "— offline"}</option>
          <option value="server">Piper</option>
          <option value="auto">Auto</option>
          <option value="browser">Browser</option>
        </select>
      </Row>

      {(tts.provider === "auto" || tts.provider === "server") && serverVoices.length > 0 && (
        <Row label="Voice">
          <select
            value={tts.serverVoice ?? ""}
            onChange={(e) => setTTS({ serverVoice: e.target.value || undefined })}
            className={selectClass}
            aria-label="Piper voice"
          >
            <option value="">Lessac · medium</option>
            {serverVoices.map((v) => (<option key={v} value={v}>{prettyPiperLabel(v)}</option>))}
          </select>
        </Row>
      )}

      {tts.provider === "kokoro" && kokoroVoices.length > 0 && (
        <div className="pv-border-soft border-t px-4 py-3 first:border-t-0">
          <div className="flex min-h-[28px] items-center justify-between gap-3 pb-2">
            <span className="text-[15px]">Voice</span>
            <button
              type="button"
              onClick={() => handlePreview(tts.serverVoice ?? "af_bella")}
              disabled={previewing !== null}
              className="pv-border rounded-full border px-3 py-1 text-[13px] leading-none hover:opacity-90 active:opacity-70 disabled:opacity-50"
              aria-label="Preview selected voice"
            >
              {previewing ? "▶ …" : "▶ Preview"}
            </button>
          </div>
          <select
            value={tts.serverVoice ?? "af_bella"}
            onChange={(e) => setTTS({ serverVoice: e.target.value || undefined })}
            className={wideSelectClass}
            aria-label="Kokoro voice"
          >
            {groupedKokoro.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.voices.map((v) => (<option key={v} value={v}>{prettyKokoroOption(v)}</option>))}
              </optgroup>
            ))}
          </select>
        </div>
      )}
    </Group>
  );

  const sleepControls = (
    <Group header="Sleep Timer">
      <Row
        label={
          sleepTimer.expiresAt && sleepRemainingMs > 0 ? (
            <span>
              Sleep{" "}
              <span className="font-semibold tabular-nums" style={{ color: ACCENT }}>
                {formatCountdown(sleepRemainingMs)}
              </span>
            </span>
          ) : (
            "Sleep"
          )
        }
      >
        <select
          value={sleepTimer.durationMinutes ?? 0}
          onChange={(e) => { const m = Number(e.target.value); armSleepTimer(m > 0 ? m : null); }}
          className={selectClass}
          aria-label="Sleep timer"
        >
          {SLEEP_OPTIONS.map((m) => (<option key={m} value={m}>{m === 0 ? "Off" : `${m} min`}</option>))}
        </select>
      </Row>
    </Group>
  );

  const hub = (
    <div className="grid grid-cols-4 gap-2 px-1 pb-2 pt-1">
      <HubButton label="Chapters" onClick={() => { onClose(); window.setTimeout(() => onOpenChapters?.(), 60); }} disabled={!onOpenChapters} icon={
        <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
      } />
      <HubButton label="View" onClick={() => setTab("view")} icon={
        <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 7V5h16v2"/><path d="M9 20h6"/><path d="M12 5v15"/></svg>
      } />
      <HubButton label="Voice" onClick={() => setTab("voice")} icon={
        <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H3v6h3l5 4V5z"/><path d="M15.5 8.5a5 5 0 0 1 0 7"/><path d="M18.5 6a8 8 0 0 1 0 12"/></svg>
      } />
      <HubButton label="Sleep" onClick={() => setTab("sleep")} icon={
        <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/></svg>
      } />
    </div>
  );

  return (
    <>
      {!inline && (
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        aria-hidden
      />
      )}
      {/* 80dvh, not 85vh (2026-07-20): on iOS Safari `vh` ignores the
          collapsed URL bar, so an 85vh sheet stood taller than the
          VISIBLE viewport — its top, sticky header and Done button
          included, hung offscreen. dvh tracks the real visible height;
          browsers without dvh ignore the inline style and keep the
          85vh class. */}
      <div
        className={inline ? "pv-fg flex min-h-0 w-full flex-col overflow-y-auto" : "pv-surface pv-border pv-fg fixed inset-x-0 z-50 mx-auto flex max-h-[70vh] w-full max-w-md flex-col overflow-y-auto rounded-2xl border pb-4"}
        /* 2026-10-06: anchored ABOVE the bar (was bottom-0, i.e. covering
           it). --pv-bar-h is published by ReaderView from the bar's
           measured height. shadow dropped: dithers badly on e-ink. */
        style={inline ? {} : {
          bottom: "calc(var(--pv-bar-h, 152px) + 8px)",
          // Was 80dvh with the sheet pinned to the bottom of the screen.
          // Now that it starts above the bar, 80dvh would run off the top,
          // so cap it against the space actually available.
          maxHeight: "calc(100dvh - var(--pv-bar-h, 152px) - 24px)",
        }}
      >
        {/* Sticky header — grabber + title + Done. Stays pinned so Done is
            always reachable even when the voice list makes the sheet scroll
            (2026-07-17: Done was going offscreen). */}
        <div className="pv-surface sticky top-0 z-10">
          <div className="flex justify-center pt-2" aria-hidden>
            <div className="pv-surface-2 h-1 w-9 rounded-full brightness-150" />
          </div>
          <div className="flex items-center justify-between gap-2 px-4 pb-2 pt-1">
            <div className="flex min-w-0 items-center gap-1">
              {tab !== null && (
                <button
                  onClick={() => setTab(null)}
                  aria-label="Back"
                  className="pv-fg-soft -ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-2xl leading-none hover:opacity-80 active:opacity-60"
                >‹</button>
              )}
              <h2 className="truncate text-xl font-bold tracking-tight">{tabTitle}</h2>
            </div>
            {/* 2026-10-07, Thomas: "Remove X on marks search and settings.
                Remove whole line."
                The X is gone. The ROW stays, deliberately and against the
                letter of the request: it also carries the back control for
                the Appearance / Voice / Sleep sub-tabs. Removing the line
                would strand him inside a sub-tab with no way back to the
                settings hub -- the same shape as the auto-hiding caret that
                left him unable to turn a page. The Settings tile still
                toggles the whole panel shut. */}
          </div>
        </div>

        <div className="px-4 pb-1 pt-1">
          {tab === null ? hub
            : tab === "view" ? viewControls
            : tab === "voice" ? voiceControls
            : sleepControls}
        </div>
      </div>
    </>
  );
}

/* ── iOS-style grouped-list building blocks ─────────────────────── */

function HubButton({ label, icon, onClick, disabled }: { label: string; icon: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="pv-surface-2 pv-border flex min-h-[80px] flex-col items-center justify-center gap-1.5 rounded-xl border px-1 py-3 hover:opacity-90 active:opacity-70 disabled:opacity-40"
    >
      <span style={{ color: ACCENT }} aria-hidden>{icon}</span>
      <span className="text-[12px] font-semibold leading-none">{label}</span>
    </button>
  );
}

function Group({ header, children }: { header: string; children: ReactNode }) {
  return (
    <section>
      <div className="pv-fg-soft px-4 pb-1.5 text-[12px] font-medium uppercase tracking-wider">
        {header}
      </div>
      <div className="pv-surface-2 pv-border overflow-hidden rounded-xl border">
        {children}
      </div>
    </section>
  );
}

function Row({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="pv-border-soft flex min-h-[44px] items-center justify-between gap-3 border-t px-4 py-2.5 first:border-t-0">
      <span className="shrink-0 text-[15px]">{label}</span>
      <div className="flex min-w-0 justify-end">{children}</div>
    </div>
  );
}

function SliderRow({ label, value, children }: { label: string; value: string; children: ReactNode }) {
  return (
    <div className="pv-border-soft border-t px-4 py-3 first:border-t-0">
      <div className="flex items-center justify-between gap-3 pb-2">
        <span className="text-[15px]">{label}</span>
        <span className="pv-fg-soft text-[15px] tabular-nums">{value}</span>
      </div>
      {children}
    </div>
  );
}

/* ── Voice helpers ──────────────────────────────────────────────── */

function prettyPiperLabel(id: string): string {
  const m = id.match(/en_US-([a-z_]+)-(low|medium|high)/i);
  if (!m) return id;
  const name = m[1].replace(/_/g, " ");
  const tier = m[2];
  const cap = name.charAt(0).toUpperCase() + name.slice(1);
  return `${cap} · ${tier}`;
}

interface KokoroInfo { lang: string; langCode: string; gender: "Female" | "Male"; name: string; raw: string; }
function decodeKokoro(id: string): KokoroInfo | null {
  const m = id.match(/^([abefhijpz])([fm])_(.+)$/);
  if (!m) return null;
  const langMap: Record<string, string> = { a: "American", b: "British", e: "Spanish", f: "French", h: "Hindi", i: "Italian", j: "Japanese", p: "Portuguese", z: "Chinese" };
  const lang = langMap[m[1]] ?? m[1].toUpperCase();
  const gender = m[2] === "f" ? "Female" : "Male";
  const name = m[3].charAt(0).toUpperCase() + m[3].slice(1);
  return { lang, langCode: m[1], gender, name, raw: id };
}

function prettyKokoroOption(id: string): string {
  const info = decodeKokoro(id);
  if (!info) return id;
  return `${info.name} (${info.lang}, ${info.gender})`;
}

function groupKokoroVoices(voices: string[]): { label: string; voices: string[] }[] {
  const buckets: Record<string, string[]> = {};
  const otherLangs: string[] = [];
  for (const v of voices) {
    const info = decodeKokoro(v);
    const key = info?.lang ?? "Other";
    if (!buckets[key]) buckets[key] = [];
    buckets[key].push(v);
    if (info && !["American", "British"].includes(info.lang) && !otherLangs.includes(info.lang)) otherLangs.push(info.lang);
  }
  const order = ["American", "British", ...otherLangs.sort(), "Other"];
  const seen = new Set<string>();
  const out: { label: string; voices: string[] }[] = [];
  for (const key of order) { if (buckets[key] && !seen.has(key)) { seen.add(key); out.push({ label: key, voices: buckets[key] }); } }
  for (const key of Object.keys(buckets)) { if (!seen.has(key)) out.push({ label: key, voices: buckets[key] }); }
  return out;
}

function formatCountdown(ms: number): string {
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
